import React, { useState, useEffect, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../../assets/images/logos/transserrano_viagens_pequeno.png';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { IconType } from 'react-icons';
import {
  FaHome,
  FaRoute,
  FaTags,
  FaUsers,
  FaImages,
  FaStar,
  FaFileAlt,
  FaQuestionCircle,
  FaIcons,
  FaStarHalfAlt,
  FaPalette,
  FaCode,
  FaChevronLeft,
  FaChevronRight,
  FaSignOutAlt,
  FaInfoCircle,
  FaThLarge,
  FaChartBar,
  FaBalanceScale,
  FaInbox,
  FaBookOpen,
  FaLightbulb,
  FaUserShield,
} from 'react-icons/fa';

/** react-icons + React 18 @types JSX compatibility */
const NavIcon: React.FC<{ icon: IconType }> = ({ icon: Icon }) => {
  const Cmp = Icon as React.ComponentType<{ 'aria-hidden'?: boolean; size?: number }>;
  return <Cmp aria-hidden size={18} />;
};

// ---------------------------------------------------------------------------
// Menu data
// ---------------------------------------------------------------------------

interface MenuItem {
  path: string;
  label: string;
  icon: IconType;
}

interface MenuGroup {
  id: string;
  label: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    id: 'geral',
    label: 'Geral',
    items: [
      { path: '/admin/dashboard', label: 'Dashboard', icon: FaHome },
      { path: '/admin/contactos', label: 'Pedidos & Reservas', icon: FaInbox },
      { path: '/admin/analytics', label: 'Analytics', icon: FaChartBar },
    ],
  },
  {
    id: 'viagens',
    label: 'Viagens',
    items: [
      { path: '/admin/viagens', label: 'Viagens', icon: FaRoute },
      { path: '/admin/categories', label: 'Categorias', icon: FaTags },
    ],
  },
  {
    id: 'pagina-principal',
    label: 'Página Principal',
    items: [
      { path: '/admin/hero', label: 'Secção Principal', icon: FaStar },
      { path: '/admin/carrossel', label: 'Carrossel', icon: FaImages },
      { path: '/admin/destaque', label: 'Destinos em Destaque', icon: FaThLarge },
    ],
  },
  {
    id: 'sobre-nos',
    label: 'Sobre Nós',
    items: [
      { path: '/admin/teammembers', label: 'Equipa', icon: FaUsers },
      { path: '/admin/content', label: 'Texto Sobre Nós', icon: FaFileAlt },
      { path: '/admin/about-gallery', label: 'Galeria', icon: FaImages },
    ],
  },
  {
    id: 'comunidade',
    label: 'Comunidade',
    items: [
      { path: '/admin/testimonials', label: 'Avaliações', icon: FaStarHalfAlt },
      { path: '/admin/faq', label: 'FAQ', icon: FaQuestionCircle },
    ],
  },
  {
    id: 'informacao-legal',
    label: 'Informação legal',
    items: [{ path: '/admin/informacao-legal', label: 'Páginas legais', icon: FaBalanceScale }],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    items: [
      { path: '/admin/guia', label: 'Guia do Site', icon: FaBookOpen },
      { path: '/admin/recomendacoes', label: 'Recomendações Técnicas', icon: FaLightbulb },
      { path: '/admin/metadados', label: 'Metadados', icon: FaCode },
      { path: '/admin/favicon', label: 'Favicon / Ícones', icon: FaIcons },
      { path: '/admin/design', label: 'Design System', icon: FaPalette },
      { path: '/admin/conta', label: 'A Minha Conta', icon: FaUserShield },
      { path: '/admin/sobre-o-sistema', label: 'Sobre o Sistema', icon: FaInfoCircle },
    ],
  },
];

function getInitialsFromEmail(email: string): string {
  const parts = email.split('@')[0].split(/[._-]/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function getEmailFromToken(): string | null {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload.email ?? payload.sub ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const AdminLayoutWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${({ theme }) => theme.semantic.color.surface.page};
`;

const Sidebar = styled.aside<{ $collapsed: boolean }>`
  width: ${({ theme, $collapsed }) =>
    $collapsed
      ? theme.componentTokens.admin.sidebarWidthCollapsed
      : theme.componentTokens.admin.sidebarWidth};
  background: ${({ theme }) => theme.semantic.color.surface.raised};
  border-right: 1px solid ${({ theme }) => theme.semantic.color.border.default};
  position: fixed;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width ${({ theme }) => theme.primitives.duration.normal}
    ${({ theme }) => theme.primitives.easing.easeOut};
  z-index: 100;
`;

const Logo = styled.div<{ $collapsed: boolean }>`
  padding: ${({ theme }) => `${theme.primitives.space[5]} ${theme.primitives.space[4]}`};
  border-bottom: 1px solid ${({ theme }) => theme.semantic.color.border.default};
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  flex-shrink: 0;
  min-height: ${({ theme }) => theme.componentTokens.admin.headerHeight};

  img {
    height: 32px;
    width: auto;
    opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
    max-width: ${({ $collapsed }) => ($collapsed ? '0' : '160px')};
    transition:
      opacity ${({ theme }) => theme.primitives.duration.fast}
        ${({ theme }) => theme.primitives.easing.easeOut},
      max-width ${({ theme }) => theme.primitives.duration.normal}
        ${({ theme }) => theme.primitives.easing.easeOut};
    pointer-events: ${({ $collapsed }) => ($collapsed ? 'none' : 'auto')};
  }
`;

const NavMenu = styled.nav`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ theme }) => `${theme.primitives.space[3]} 0`};

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.semantic.color.border.default};
    border-radius: ${({ theme }) => theme.primitives.radius.full};
  }
`;

const NavGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.primitives.space[3]};
`;

const GroupLabel = styled.span<{ $active: boolean; $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  font-size: ${({ theme }) => theme.primitives.fontSize.xs};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
  letter-spacing: ${({ theme }) => theme.primitives.letterSpacing.wide};
  text-transform: uppercase;
  color: ${({ theme, $active }) =>
    $active ? theme.semantic.color.brand.primary : theme.semantic.color.text.tertiary};
  padding: ${({ theme }) =>
    `${theme.primitives.space[2]} ${theme.primitives.space[4]} ${theme.primitives.space[1]}`};
  transition: color ${({ theme }) => theme.primitives.duration.fast}
    ${({ theme }) => theme.primitives.easing.easeOut};
`;

const GroupItems = styled.ul`
  list-style: none;
  padding: 0 ${({ theme }) => theme.primitives.space[2]};
  margin: 0;
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.primitives.space[3]};
  padding: ${({ theme }) => `${theme.primitives.space[2]} ${theme.primitives.space[3]}`};
  color: ${({ theme, $active }) =>
    $active ? theme.semantic.color.brand.primary : theme.semantic.color.text.secondary};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.primitives.radius.md};
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  font-weight: ${({ theme, $active }) =>
    $active ? theme.primitives.fontWeight.semibold : theme.primitives.fontWeight.medium};
  background: ${({ theme, $active }) =>
    $active ? theme.semantic.color.brand.subtle : 'transparent'};
  min-height: ${({ theme }) => theme.primitives.space[10]};
  white-space: nowrap;
  overflow: visible;
  position: relative;
  transition:
    background-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut},
    color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut};

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    opacity: 0.85;
  }

  &:hover {
    background: ${({ theme }) => theme.semantic.color.surface.overlay};
    color: ${({ theme }) => theme.semantic.color.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }
`;

const NavBadge = styled.span<{ $collapsed: boolean }>`
  margin-left: auto;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.semantic.color.feedback.error};
  color: ${({ theme }) => theme.semantic.color.text.inverse};
  font-size: ${({ theme }) => theme.primitives.fontSize.xs};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.bold};
  border-radius: ${({ theme }) => theme.primitives.radius.full};
  line-height: 1;

  ${({ $collapsed }) =>
    $collapsed &&
    `
      position: absolute;
      top: 4px;
      right: 4px;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      font-size: 10px;
    `}
`;

const CollapseToggle = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.primitives.space[2]};
  flex: 1;
  margin: ${({ theme }) => theme.primitives.space[2]} ${({ theme }) => theme.primitives.space[3]};
  padding: ${({ theme }) => `${theme.primitives.space[2]} ${theme.primitives.space[3]}`};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.semantic.color.border.default};
  border-radius: ${({ theme }) => theme.primitives.radius.md};
  color: ${({ theme }) => theme.semantic.color.text.secondary};
  cursor: pointer;
  flex-shrink: 0;
  min-height: 44px;
  font-size: ${({ theme }) => theme.primitives.fontSize.xs};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.medium};
  white-space: nowrap;
  overflow: hidden;
  transition:
    background-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut},
    color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut},
    border-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut};

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.semantic.color.surface.overlay};
    color: ${({ theme }) => theme.semantic.color.text.primary};
    border-color: ${({ theme }) => theme.semantic.color.border.strong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }
`;

const CollapseArea = styled.div`
  border-top: 1px solid ${({ theme }) => theme.semantic.color.border.default};
  padding: ${({ theme }) => theme.primitives.space[2]} 0;
  flex-shrink: 0;
  display: flex;
`;

const TopBar = styled.header<{ $collapsed: boolean }>`
  position: fixed;
  top: 0;
  left: ${({ theme, $collapsed }) =>
    $collapsed
      ? theme.componentTokens.admin.sidebarWidthCollapsed
      : theme.componentTokens.admin.sidebarWidth};
  right: 0;
  height: ${({ theme }) => theme.componentTokens.admin.headerHeight};
  background: ${({ theme }) => theme.semantic.color.surface.raised};
  border-bottom: 1px solid ${({ theme }) => theme.semantic.color.border.default};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 ${({ theme }) => theme.componentTokens.admin.contentPaddingX};
  gap: ${({ theme }) => theme.primitives.space[3]};
  z-index: 90;
  transition: left ${({ theme }) => theme.primitives.duration.normal}
    ${({ theme }) => theme.primitives.easing.easeOut};
`;

const TopBarUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.primitives.space[2]};
  min-width: 0;
`;

const TopBarEmail = styled.span`
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  color: ${({ theme }) => theme.semantic.color.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const Avatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: ${({ theme }) => theme.primitives.radius.full};
  background: ${({ theme }) => theme.semantic.color.brand.primary};
  color: ${({ theme }) => theme.semantic.color.text.inverse};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.primitives.fontSize.xs};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.semibold};
  flex-shrink: 0;
`;

const TopBarLogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.primitives.space[2]};
  height: 36px;
  padding: 0 ${({ theme }) => theme.primitives.space[3]};
  border-radius: ${({ theme }) => theme.primitives.radius.md};
  border: 1px solid ${({ theme }) => theme.semantic.color.border.default};
  background: transparent;
  color: ${({ theme }) => theme.semantic.color.text.secondary};
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  font-weight: ${({ theme }) => theme.primitives.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut},
    color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut},
    border-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut};

  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.semantic.color.surface.overlay};
    color: ${({ theme }) => theme.semantic.color.text.primary};
    border-color: ${({ theme }) => theme.semantic.color.border.strong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }
`;

const TopBarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.semantic.color.border.default};
  flex-shrink: 0;
`;

const MainContent = styled.main<{ $collapsed: boolean }>`
  flex: 1;
  margin-left: ${({ theme, $collapsed }) =>
    $collapsed
      ? theme.componentTokens.admin.sidebarWidthCollapsed
      : theme.componentTokens.admin.sidebarWidth};
  padding-top: ${({ theme }) => theme.componentTokens.admin.headerHeight};
  min-width: 0;
  transition: margin-left ${({ theme }) => theme.primitives.duration.normal}
    ${({ theme }) => theme.primitives.easing.easeOut};
`;

const AdminContentColumn = styled.div`
  max-width: ${({ theme }) => theme.componentTokens.admin.contentMaxWidth};
  margin: 0 auto;
  width: 100%;
  min-width: 0;
  padding-left: ${({ theme }) => theme.componentTokens.admin.contentPaddingX};
  padding-right: ${({ theme }) => theme.componentTokens.admin.contentPaddingX};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem('admin-sidebar-collapsed') === 'true'
  );

  // Count of pending requests, shown as a badge on "Pedidos & Reservas".
  const [pedidosPorResponder, setPedidosPorResponder] = useState<number>(0);

  const fetchPedidosCount = useCallback(async () => {
    try {
      const res = await api.get('/admin/contactos/count');
      setPedidosPorResponder(res.data?.por_responder ?? 0);
    } catch {
      /* silent — the badge is non-critical chrome */
    }
  }, []);

  useEffect(() => {
    fetchPedidosCount();
    // The Pedidos page fires this after a status change so the badge stays fresh.
    const handler = () => fetchPedidosCount();
    window.addEventListener('pedidos:refresh', handler);
    return () => window.removeEventListener('pedidos:refresh', handler);
  }, [fetchPedidosCount]);

  const email = getEmailFromToken();
  const initials = email ? getInitialsFromEmail(email) : 'A';

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('admin-sidebar-collapsed', String(next));
  };

  const isItemActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isGroupActive = (group: MenuGroup) => group.items.some((item) => isItemActive(item.path));

  return (
    <AdminLayoutWrapper
      style={
        {
          '--admin-sidebar-current-width': collapsed
            ? theme.componentTokens.admin.sidebarWidthCollapsed
            : theme.componentTokens.admin.sidebarWidth,
        } as React.CSSProperties & { '--admin-sidebar-current-width': string }
      }
    >
      <Sidebar $collapsed={collapsed}>
        <Logo $collapsed={collapsed}>
          <img src={logoImage} alt="Trans Serrano Admin" />
        </Logo>

        <NavMenu>
          {menuGroups.map((group) => {
            const groupActive = isGroupActive(group);
            return (
              <NavGroup key={group.id}>
                <GroupLabel $active={groupActive} $collapsed={collapsed}>
                  {group.label}
                </GroupLabel>
                <GroupItems>
                  {group.items.map((item) => {
                    const active = isItemActive(item.path);
                    return (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          $active={active}
                          title={collapsed ? item.label : undefined}
                        >
                          <NavIcon icon={item.icon} />
                          {!collapsed && item.label}
                          {item.path === '/admin/contactos' && pedidosPorResponder > 0 && (
                            <NavBadge
                              $collapsed={collapsed}
                              aria-label={`${pedidosPorResponder} pedidos por responder`}
                            >
                              {pedidosPorResponder}
                            </NavBadge>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </GroupItems>
              </NavGroup>
            );
          })}
        </NavMenu>

        <CollapseArea>
          <CollapseToggle
            type="button"
            $collapsed={collapsed}
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <NavIcon icon={collapsed ? FaChevronRight : FaChevronLeft} />
          </CollapseToggle>
        </CollapseArea>
      </Sidebar>

      <MainContent $collapsed={collapsed}>
        <TopBar $collapsed={collapsed}>
          <TopBarUserInfo>
            <Avatar aria-hidden>{initials}</Avatar>
            <TopBarEmail title={email ?? 'Administrador'}>{email ?? 'Administrador'}</TopBarEmail>
          </TopBarUserInfo>
          <TopBarDivider />
          <TopBarLogoutButton type="button" onClick={handleLogout} aria-label="Sair">
            <NavIcon icon={FaSignOutAlt} />
            Sair
          </TopBarLogoutButton>
        </TopBar>

        <AdminContentColumn>{children}</AdminContentColumn>
      </MainContent>
    </AdminLayoutWrapper>
  );
};

export default AdminLayout;
